import { LOGIN_TIMEOUT_MS } from "@/utils/loginRequest";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const repsApi = createApi({
  reducerPath: "repsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/reps/`,
    prepareHeaders: (headers, { getState }) => {
      const cookies = document.cookie.split(";");
      let jwtCookie = null;

      cookies.forEach((cookie) => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith("token=")) {
          jwtCookie = "Bearer " + trimmedCookie.split("token=")[1];
        }
      });

      if (jwtCookie) {
        headers.set("authorization", jwtCookie);
      }

      return headers;
    },
  }),
  tagTypes: ['RepDoctorFavourites', 'RepProducts', 'RepCompanies'],
  endpoints: (builder) => ({
    repsSignup: builder.mutation({
      query: (body) => ({
        url: `signup`,
        method: "POST",
        body: body,
      }),
    }),
    verifyTwoFactorOtp: builder.mutation({
      query: (body) => ({
        url: `verify-2factor-reps`,
        method: "POST",
        body: body,
      }),
    }),
    repsSignin: builder.mutation({
      query: (body) => ({
        url: `signin`,
        method: "POST",
        timeout: LOGIN_TIMEOUT_MS,
        body: body,
      }),
    }),
    repProfileImage: builder.mutation({
      query: (body) => ({
        url: `rep-profile-image`,
        method: "POST",
        body: body,
      }),
    }),
    updateRepInfo: builder.mutation({
      query: ({id, ...body}) => ({
        url: `update-rep-info/${id}`,
        method: "PUT",
        body:body
      }),
    }),
    updatePassword: builder.mutation({
      query: ({id, ...body}) => ({
        url: `update-password/${id}`,
        method: "PUT",
        body:body
      }),
    }),
    repsCoverImage: builder.mutation({
      query: (body) => ({
        url: `reps-cover-image`,
        method: "POST",
        body:body
      }),
    }),
    repsProfile: builder.mutation({
      query: (body) => ({
        url: `account-setup`,
        method: "POST",
        body: body,
      }),
    }),
    companyDetails: builder.mutation({
      query: (body) => ({
        url: `company-details`,
        method: "POST",
        body: body,
      }),
    }),
    repsList: builder.query({
      query: (body) => ({
        url: `reps-list?${body?.page ? 'page=' + body?.page : ''}&${body?.searchQuery ? 'search=' + body?.searchQuery : ''}`,
        method: "GET",
      }),
    }),
    workingDetail: builder.mutation({
      query: (body) => ({
        url: `working-details`,
        method: "POST",
        body: body,
      }),
    }),
    addDoctorFavourite: builder.mutation({
      query: (body) => ({
        url: `add-favourite-doctor`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: ['RepDoctorFavourites'],
    }),
    updateDoctorFavourite: builder.mutation({
      query: ({id, ...body}) => ({
        url: `update-favourite-doctors/${id}`,
        method: "PUT",
        body:body
      }),
      invalidatesTags: ['RepDoctorFavourites'],
    }),
    removeDoctorFavourite: builder.mutation({
      query: (body) => ({
        url: `remove-favourite-doctor`,
        method: "DELETE",
        body: body,
      }),
      invalidatesTags: ['RepDoctorFavourites'],
    }),
    favouriteList: builder.query({
      query: () => ({
        url: `favourite-doctors`,
        method: "GET",
      }),
      providesTags: ['RepDoctorFavourites'],
    }),

    favouriteDoctorsList: builder.query({
      query: (body) => {
        if (!body.id) throw new Error("ID is required");
        const params = new URLSearchParams();
        params.set('id', body.id);
        if (body?.sortBy) params.set('sortBy', body.sortBy);
        if (body?.sortOrder) params.set('sortOrder', body.sortOrder);
        if (body?.page) params.set('page', String(body.page));
        if (body?.limit) params.set('limit', String(body.limit));
        return {
          url: `favourite-doctors?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ['RepDoctorFavourites'],
      serializeQueryArgs: ({ queryArgs }) => {
        return `${queryArgs?.id}-${queryArgs?.sortBy}-${queryArgs?.sortOrder}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg?.page === 1) return newItems;
        return {
          ...newItems,
          data: [...(currentCache?.data || []), ...(newItems?.data || [])],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.page !== previousArg?.page
          || currentArg?.sortBy !== previousArg?.sortBy
          || currentArg?.sortOrder !== previousArg?.sortOrder;
      },
    }),
    sendOtp: builder.mutation({
      query: (body) => ({
        url: `send-otp`,
        method: "POST",
        body:body
      }),
    }),
    addRepsProduct: builder.mutation({
      query: (body) => ({
        url: `add-product-request`,
        method: "POST",
        body:body
      }),
      invalidatesTags: ['RepProducts'],
    }),
    removeRepsProduct: builder.mutation({
      query: (productId) => ({
        url: `my-products/${productId}`,
        method: "DELETE",
      }),
    }),
    repCompanies: builder.query({
      query: () => ({
        url: `companies`,
        method: "GET",
      }),
      providesTags: ['RepCompanies'],
    }),
    addRepCompany: builder.mutation({
      query: (manufacturerId) => ({
        url: `companies`,
        method: "POST",
        body: { manufacturerId },
      }),
    }),
    removeRepCompany: builder.mutation({
      query: (manufacturerId) => ({
        url: `companies/${manufacturerId}`,
        method: "DELETE",
      }),
    }),
    getProductsList: builder.query({
      query: (id) => ({
        url: `product-lists?id=${id}`,
        method: "GET",
      }),
      providesTags: ['RepProducts'],
    }),
    getProductsListWithDetails: builder.query({
      query: ({id, sortBy, sortOrder, page, limit}) => {
        const params = new URLSearchParams();
        params.set('id', id);
        if (sortBy) params.set('sortBy', sortBy);
        if (sortOrder) params.set('sortOrder', sortOrder);
        if (page) params.set('page', String(page));
        if (limit) params.set('limit', String(limit));
        return {
          url: `get-product-lists?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ['RepProducts'],
      serializeQueryArgs: ({ queryArgs }) => {
        return `${queryArgs?.id}-${queryArgs?.sortBy}-${queryArgs?.sortOrder}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg?.page === 1) return newItems;
        return {
          ...newItems,
          data: {
            success: [...(currentCache?.data?.success || []), ...(newItems?.data?.success || [])],
            pending: [...(currentCache?.data?.pending || []), ...(newItems?.data?.pending || [])],
          },
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.page !== previousArg?.page
          || currentArg?.sortBy !== previousArg?.sortBy
          || currentArg?.sortOrder !== previousArg?.sortOrder;
      },
    }),
    findRepsFromLocation: builder.query({
      query: (body) => ({
        url: `find-reps`,
        method: 'POST',
        body: body
      }),
    }),
  }),
});

export const {
  useRepsSignupMutation,
  useRepsSigninMutation,
  useUpdateRepInfoMutation,
  useUpdatePasswordMutation,
  useRepsCoverImageMutation,
  useRepsProfileMutation,
  useCompanyDetailsMutation,
  useRepsListQuery,
  useRepProfileImageMutation,
  useWorkingDetailMutation,
  useAddDoctorFavouriteMutation,
  useUpdateDoctorFavouriteMutation,
  useRemoveDoctorFavouriteMutation,
  useFavouriteListQuery,
  useFavouriteDoctorsListQuery,
  useVerifyTwoFactorOtpMutation,
  useSendOtpMutation,
  useAddRepsProductMutation,
  useRemoveRepsProductMutation,
  useRepCompaniesQuery,
  useAddRepCompanyMutation,
  useRemoveRepCompanyMutation,
  useGetProductsListQuery,
  useGetProductsListWithDetailsQuery,
  useFindRepsFromLocationQuery
} = repsApi;
